import React, { useState, useRef, useEffect, lazy, Suspense } from 'react';
import Button from '../../composants/Button/Button';
import TabNav from '../../composants/TabNav/TabNav';
import ChangeRole from '../../composants/ChangeRole/ChangeRole';
import MesDocuments from '../../composants/MesDocuments/MesDocuments';
import GestionComptes from '../../composants/GestionComptes/GestionComptes';
import { useAuth } from '../../contexts/AuthContext'; // Ajout de l'import pour accéder au contexte d'authentification
import {
  isClientRole,
  isPrestataireRole,
  isAdminRole
} from '../../utils/roleUtils'; // Import des fonctions de utils/roleUtils
import './Profil.css';

const TABS = [
  "Mes Coordonnées",
  "Droits",
  "Mes documents",
  "Mon équipe"
];

const DOCUMENT_TABS = [
  "Logo entreprise",
  "Pièces du marché",
  "Assurance",
  "Attestation fiscale et sociale",
  "Certifications et attestations"
];

type ContactKeys = 'nom' | 'prenom' | 'telephone' | 'fonction';

type OrganismeKeys = 'nom' | 'siret' | 'adresse' | 'ville' | 'codePostal';

interface Contact {
  nom: string;
  prenom: string;
  email: string;
  telephone: string;       // Téléphone de contact
  fonction: string; // Nouvelle propriété
  isEditing: boolean;
  role: string;
}

interface Organisme {
  nom: string;
  siret: string;
  adresse: string;
  ville: string;
  codePostal: string;
  isEditing: boolean;
  id?: string | number; // <-- ajout
}

interface ProfileData {
  logo: string;
  entreprise: string;
  contact: Contact;
  organisme: Organisme;
}

const Profil: React.FC = () => {
  // Récupération des informations utilisateur depuis le contexte d'authentification
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<number>(0);
  const [activeDocTab, setActiveDocTab] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [phoneConnexionError, setPhoneConnexionError] = useState<string | null>(null); // Ajout d'un état pour les erreurs de validation du téléphone de connexion
  const [siretError, setSiretError] = useState<string | null>(null); // Ajout d'un état pour l'erreur de SIRET

  // Données du profil
  const [profileData, setProfileData] = useState<ProfileData>({
    logo: '',
    entreprise: "Nom de l'entreprise",
    contact: {
      nom: '',
      prenom: '',
      email: '',
      telephone: '',
      fonction: '',
      isEditing: false,
      role: user?.role || '' // Utiliser directement le rôle de l'utilisateur connecté
    },
    organisme: {
      nom: '',
      siret: '',
      adresse: '',
      ville: '',
      codePostal: '',
      isEditing: false,
      id: undefined
    }
  });

  console.log('Profil utilisateur:', user);
  console.log('Role', user?.role);

  const getContactUpdateRoute = (role: string) => {
    const apiUrl = process.env.REACT_APP_API_URL || '';
    switch (role) {
      case 'Acheteur signataire admin':
        return `${apiUrl}/responsables-achat/me`;
      case 'Acheteur signataire':
        return `${apiUrl}/validateurs-achat/me`;
      case 'Acheteur':
        return `${apiUrl}/acheteurs/me`;
      case 'Prestataire signataire admin':
        return `${apiUrl}/gestionnaires/me`;
      case 'Prestataire signataire':
        return `${apiUrl}/signataires/me`;
      case 'Prestataire':
        return `${apiUrl}/interlocuteurs/me`;
      default:
        return '';
    }
  };

  // Charger les données de l'utilisateur depuis l'API
  useEffect(() => {
    const fetchUserData = async () => {
      setLoading(true);
      setError(null);

      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('Aucun token d\'authentification trouvé');

        // 1. Récupérer les infos de connexion via /me
        const meResponse = await fetch(`${process.env.REACT_APP_API_URL}/me`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          }
        });

        if (!meResponse.ok) throw new Error('Erreur lors de la récupération des informations de connexion');

        const meDataRaw = await meResponse.json();
        const meData = meDataRaw.data;

        // 2. Récupérer les infos utilisateur métier via la route spécifique au rôle
        const userRoute = getContactUpdateRoute(user?.role || '');
        let userData: any = {};
        let idOrganisme = '';
        let organismeData = {
          nom: '',
          siret: '',
          adresse: '',
          ville: '',
          codePostal: '',
          isEditing: false,
          id: undefined
        };

        if (userRoute) {
          const userResponse = await fetch(userRoute, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          });

          if (userResponse.ok) {
            const userDataRaw = await userResponse.json();
            userData = userDataRaw.data;
            
            // Pour les prestataires, c'est id_entite, pour les acheteurs, c'est id_organisme
            idOrganisme = userData?.id_organisme || userData?.id_entite;

            // Récupérer nom, siret et id selon le rôle
            if (isPrestataireRoleLocal(user?.role)) {
              // Pour les prestataires, les informations sont dans 'entite'
              if (userData.entite) {
                organismeData.nom = userData.entite.nom || '';
                organismeData.id = userData.entite.id;
                // Pour les entités, les informations de SIRET sont dans les entreprises associées
                // Nous prendrons la première entreprise pour cet exemple
                if (userData.entite.entreprises && userData.entite.entreprises.length > 0) {
                  organismeData.siret = String(userData.entite.entreprises[0].siret) || '';
                }
              }
            } else {
              // Pour les acheteurs, les informations sont dans 'organisme'
              if (userData.organisme) {
                organismeData.nom = userData.organisme.nom || '';
                organismeData.siret = userData.organisme.siret || '';
                organismeData.id = userData.organisme.id;
              }
            }
          }
        }

        // 3. Récupérer adresse, ville et codePostal via la bonne route en fonction du rôle
        if (idOrganisme) {
          const apiUrl = process.env.REACT_APP_API_URL || '';
          // Utiliser entites pour les prestataires, organismes pour les acheteurs
          const routeType = isPrestataireRoleLocal(user?.role) ? 'entites' : 'organismes';
          const orgResponse = await fetch(`${apiUrl}/${routeType}/${idOrganisme}`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            }
          });

          if (orgResponse.ok) {
            const orgData = await orgResponse.json();
            if (orgData.status === 'success' && orgData.data) {
              const adresseObj = orgData.data.adresse;
              organismeData.adresse = adresseObj
                ? `${adresseObj.numero_voie || ''} ${adresseObj.type_voie || ''} ${adresseObj.nom_voie || ''}${adresseObj.arrondissement ? ', ' + adresseObj.arrondissement : ''}`
                : '';
              organismeData.ville = adresseObj?.ville || '';
              organismeData.codePostal = adresseObj?.code_postal || '';
            }
          }
        }

        setProfileData(prev => ({
          ...prev,
          contact: {
            ...prev.contact,
            nom: userData?.nom || prev.contact.nom,
            prenom: userData?.prenom || prev.contact.prenom,
            email: meData.email || '',
            role: user?.role || '',
            telephone: userData?.telephone || prev.contact.telephone,
            fonction: userData?.fonction || prev.contact.fonction
          },
          organisme: organismeData
        }));

      } catch (error) {
        console.error('Erreur lors de la récupération des informations utilisateur:', error);
        setError('Impossible de récupérer les informations utilisateur.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [user?.role]); // Dépendance au rôle utilisateur

  // Navigation rapide retour
  const handleBackToProfil = () => {
    setActiveDocTab(null);
    setActiveTab(0);
  };

  // Fonction pour vérifier si l'utilisateur est autorisé à modifier ses informations
  const canEditInfo = () => {
    const role = user?.role || '';
    // Interlocuteur et Acheteur ne peuvent pas modifier les infos
    if (role === 'Prestataire' || role === 'Acheteur') {
      return false;
    }
    return true;
  };
  
  // Fonction pour vérifier si l'utilisateur peut modifier les informations de l'organisme
  const canEditOrganisme = () => {
    const role = user?.role || '';
    // Seul le responsable achat peut modifier les infos de l'organisme
    // Les prestataires ne peuvent jamais modifier les informations de l'entreprise (engagement contractuel de 2 ans)
    return role === 'Acheteur signataire admin' && !isPrestataireRoleLocal(role);
  };

  // Fonction pour déterminer le type d'action pour le bouton de modification du profil
  const getProfileActionType = () => {
    const role = user?.role || '';
    
    if (role === 'Acheteur' || role === 'Prestataire') {
      return 'demande'; // "faire une demande" pour les acheteurs et prestataires simples
    } else if (['Acheteur signataire', 'Acheteur signataire admin', 'Prestataire signataire', 'Prestataire signataire admin'].includes(role)) {
      return 'passation'; // "passation" pour les validateurs, responsables achat, signataires et gestionnaires
    } else {
      return 'demande'; // par défaut "faire une demande" pour les autres
    }
  };

  // Modifier la fonction de validation du téléphone
  const validatePhoneNumber = (phoneNumber: string, role: string = user?.role || '') => {
    const cleanNumber = phoneNumber.replace(/\s+/g, '').replace(/[^0-9]/g, '');
    // Seuls certains rôles doivent commencer par 06 ou 07
    const rolesWithMobileConstraint = [
      'Acheteur signataire admin',
      'Acheteur signataire',
      'Prestataire signataire admin',
      'Prestataire signataire'
    ];
    if (rolesWithMobileConstraint.includes(role)) {
      return /^(06|07)\d{8}$/.test(cleanNumber);
    }
    // Pour les autres rôles, on accepte n'importe quel numéro de 10 chiffres
    return /^\d{10}$/.test(cleanNumber);
  };

  // Ajouter en haut avec les autres fonctions
  const validateSiret = (siret: string) => {
    // Un SIRET valide contient 14 chiffres
    return /^\d{14}$/.test(siret.replace(/\s/g, ''));
  };

  // Onglet coordonnées
  const renderCoordonnees = () => (
    <>
      <section className="profil-tab-page">
        {loading ? (
          <p>Chargement des informations...</p>
        ) : error ? (
          <p className="error-message">{error}</p>
        ) : (
          <>
            {/* Première section: Mes informations de contact */}
            <h2 className='section-header'>Mes informations de contact</h2>
            <div className="section-content">
              <div className="form-group identifiant-connexion">
                <label>Identifiant de connexion</label>
                <div className="identifiant-connexion-input">
                  <input
                    type="email"
                    value={profileData.contact.email}
                    className="input-inactif"
                  />
                  <Button
                    value="Envoyer une demande de modification"
                    variant="primary"
                    size="small"
                    onClick={() => {
                      // Fonction pour envoyer une demande de modification
                      const sendModificationRequest = async () => {
                        try {
                          // Simuler un appel API
                          const token = localStorage.getItem('token');

                          alert("Votre demande de modification a été envoyée à votre gestionnaire de compte.");
                        } catch (error) {
                          console.error("Erreur lors de l'envoi de la demande:", error);
                          alert("Une erreur est survenue lors de l'envoi de votre demande.");
                        }
                      };

                      sendModificationRequest();
                    }}
                  />
                </div>
              </div>
              <br />
              <hr />
              <br />
              <div className="form-row grid-4">
                <div className="form-group">
                  <label>Nom</label>
                  <input
                    type="text"
                    value={profileData.contact.nom}
                  // tabIndex={-1}
                  />
                </div>

                <div className="form-group">
                  <label>Prénom</label>
                  <input
                    type="text"
                    value={profileData.contact.prenom}
                  // tabIndex={-1}
                  />
                </div>
                <div className="form-group span-2">
                  <label>Fonction</label>
                  <input
                    type="text"
                    value={profileData.contact.fonction}
                    onChange={e => setProfileData(prev => ({
                      ...prev,
                      contact: { ...prev.contact, fonction: e.target.value }
                    }))}
                  />
                </div>

                <div className="form-group span-2">
                  <label>{isClientRoleLocal(user?.role) ? "Numéro de téléphone acheteur" : "Téléphone"}</label>
                  <input
                    type="text"
                    value={profileData.contact.telephone}
                    onChange={e => {
                      const newPhone = e.target.value;
                      setProfileData(prev => ({
                        ...prev,
                        contact: { ...prev.contact, telephone: newPhone }
                      }));
                      if (newPhone.trim()) {
                        if (!validatePhoneNumber(newPhone, user?.role || '')) {
                          setPhoneError(
                            ['Acheteur signataire admin', 'Acheteur signataire', 'Prestataire signataire admin', 'Prestataire signataire'].includes(user?.role || '')
                              ? "Le numéro doit commencer par 06 ou 07 et contenir 10 chiffres"
                              : "Le numéro doit contenir 10 chiffres"
                          );
                        } else {
                          setPhoneError(null);
                        }
                      } else {
                        setPhoneError(null);
                      }
                    }}
                    className={phoneError ? "input-error" : ""}
                  />
                  {phoneError && <p className="error-message">{phoneError}</p>}
                </div>

                <div className="form-group span-2">
                  <label>Email</label>
                  <input
                    type="email"
                    value={profileData.contact.email}
                    onChange={e => setProfileData(prev => ({
                      ...prev,
                      contact: { ...prev.contact, email: e.target.value }
                    }))}
                  />
                </div>
              </div>

              {/* Bouton de mise à jour pour les informations de contact */}
              {canEditInfo() ? (
                <div className="button-container">
                  <Button
                    value={getProfileActionType() === 'passation' ? "Passation" : "Mettre à jour"}
                    variant="primary"
                    size="small"
                    onClick={() => {
                      if (profileData.contact.telephone && !validatePhoneNumber(profileData.contact.telephone)) {
                        setPhoneError(
                          ['Acheteur signataire admin', 'Acheteur signataire', 'Prestataire signataire admin', 'Prestataire signataire'].includes(user?.role || '')
                            ? "Le numéro doit commencer par 06 ou 07 et contenir 10 chiffres"
                            : "Le numéro doit contenir 10 chiffres"
                        );
                        return;
                      }

                      // Si c'est une passation, afficher un formulaire
                      if (getProfileActionType() === 'passation') {
                        // Créer un élément modal ou formulaire pour la passation
                        const passerCompte = () => {
                          const nom = prompt("Nom du remplaçant :");
                          const prenom = prompt("Prénom du remplaçant :");
                          const email = prompt("Email du remplaçant :");
                          const telephone = prompt("Téléphone du remplaçant :");
                          
                          if (nom && prenom && email && telephone) {
                            // Récupérer le rôle actuel pour le maintenir
                            const roleActuel = user?.role || '';
                            
                            // Simuler la création d'un compte en attente de validation
                            let nouveauRole = roleActuel;
                            
                            // Ici, dans un second temps, vous implémenteriez l'envoi d'un mail de validation
                            alert(`Demande de passation envoyée pour ${prenom} ${nom} (${email}). Le compte sera créé après validation par mail avec le rôle ${nouveauRole}.`);
                          } else {
                            alert("Tous les champs sont requis pour la passation de compte.");
                          }
                        };
                        
                        passerCompte();
                        return;
                      }

                      const updateContactInfo = async () => {
                        try {
                          const token = localStorage.getItem('token');
                          const route = getContactUpdateRoute(user?.role || '');
                          if (!route) {
                            alert("Impossible de déterminer la route de mise à jour pour ce rôle.");
                            return;
                          }
                          const response = await fetch(route, {
                            method: 'PUT',
                            headers: {
                              'Authorization': `Bearer ${token}`,
                              'Content-Type': 'application/json'
                            },
                            body: JSON.stringify({
                              telephone: profileData.contact.telephone,
                              email_contact: profileData.contact.email,
                              fonction: profileData.contact.fonction
                            })
                          });
                          if (!response.ok) {
                            throw new Error('Erreur lors de la mise à jour des informations de contact');
                          }
                          alert('Vous avez bien modifié vos informations');
                        } catch (error) {
                          console.error('Erreur:', error);
                          alert('Une erreur est survenue lors de la mise à jour des informations');
                        }
                      };
                      updateContactInfo();
                    }}
                  />
                </div>
              ) : (
                <div className="button-container">
                  <Button
                    value="Faire une demande"
                    variant="primary"
                    size="small"
                    onClick={() => {
                      const destinataire = isClientRoleLocal(user?.role) 
                        ? "au responsable achat" 
                        : "au gestionnaire";
                        
                      // Simuler l'envoi d'une demande au responsable approprié
                      alert(`Votre demande de modification a été envoyée ${destinataire}.`);
                    }}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </section>

      {/* Deuxième section: Mes informations de connexion */}
      <section className="profil-tab-page">

        {/* Bouton pour envoyer une demande de modification - visible pour tous */}

      </section>

      {/* Section Coordonnées d'entreprise reste inchangée */}
      <section className="profil-tab-page">
        <h2 className='section-header'>Coordonnées de l'organisme</h2>
        <div className="section-content">
          <div className="form-row grid-2">
            {/* Nom de l'organisme sur une colonne */}
            <div className="form-group">
              <label>Nom de l'organisme</label>
              <input
                type="text"
                value={profileData.organisme?.nom || ''} // Ajout du "?" et valeur par défaut
                onChange={e => setProfileData(prev => ({
                  ...prev,
                  organisme: { ...(prev.organisme || {}), nom: e.target.value }
                }))}
                disabled={!canEditOrganisme()}
                className={!canEditOrganisme() ? "input-inactif" : ""}
              />
            </div>

            {/* SIRET sur une colonne */}
            <div className="form-group">
              <label>Siret</label>
              <input
                type="text"
                value={profileData.organisme.siret}
                onChange={e => {
                  const newSiret = e.target.value;

                  setProfileData(prev => ({
                    ...prev,
                    organisme: { ...prev.organisme, siret: newSiret }
                  }));

                  if (newSiret.trim() && !validateSiret(newSiret)) {
                    setSiretError("Le SIRET doit contenir 14 chiffres");
                  } else {
                    setSiretError(null);
                  }
                }}
                className={!canEditOrganisme() ? "input-inactif" : siretError ? "input-error" : ""}
                disabled={!canEditOrganisme()}
              />
              {siretError && <p className="error-message">{siretError}</p>}
            </div>

            {/* Adresse sur deux colonnes */}
            <div className="form-group span-2">
              <label>Adresse</label>
              <input
                type="text"
                value={profileData.organisme.adresse}
                onChange={e => setProfileData(prev => ({
                  ...prev,
                  organisme: { ...prev.organisme, adresse: e.target.value }
                }))}
                disabled={!canEditOrganisme()}
                className={!canEditOrganisme() ? "input-inactif" : ""}
              />
            </div>

            {/* Code postal sur une colonne */}
            <div className="form-group">
              <label>Code Postal</label>
              <input
                type="text"
                value={profileData.organisme.codePostal}
                onChange={e => setProfileData(prev => ({
                  ...prev,
                  organisme: { ...prev.organisme, codePostal: e.target.value }
                }))}
                disabled={!canEditOrganisme()}
                className={!canEditOrganisme() ? "input-inactif" : ""}
              />
            </div>

            {/* Ville sur une colonne */}
            <div className="form-group">
              <label>Ville</label>
              <input
                type="text"
                value={profileData.organisme.ville}
                onChange={e => setProfileData(prev => ({
                  ...prev,
                  organisme: { ...prev.organisme, ville: e.target.value }
                }))}
                disabled={!canEditOrganisme()}
                className={!canEditOrganisme() ? "input-inactif" : ""}
              />
            </div>
          </div>

          {/* Bouton de mise à jour des infos d'entreprise - utilisation de canEditOrganisme() */}
          {canEditOrganisme() ? (
            <div className="button-container">
              <Button
                value="Mettre à jour"
                variant="primary"
                size="small"
                onClick={() => {
                  if (profileData.organisme.siret && !validateSiret(profileData.organisme.siret)) {
                    setSiretError("Le SIRET doit contenir 14 chiffres");
                    return;
                  }
                  const updateOrganismeInfo = async () => {
                    try {
                      const token = localStorage.getItem('token');
                      const id = profileData.organisme.id;
                      if (!id) {
                        alert("Impossible de déterminer l'organisme à mettre à jour.");
                        return;
                      }
                      
                      const apiUrl = process.env.REACT_APP_API_URL || '';
                      // Utiliser entites pour les prestataires, organismes pour les acheteurs
                      const routeType = isPrestataireRoleLocal(user?.role) ? 'entites' : 'organismes';
                      
                      const response = await fetch(`${apiUrl}/${routeType}/${id}`, {
                        method: 'PUT',
                        headers: {
                          'Authorization': `Bearer ${token}`,
                          'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                          nom: profileData.organisme.nom,
                          siret: profileData.organisme.siret,
                          adresse: profileData.organisme.adresse,
                          ville: profileData.organisme.ville,
                          codePostal: profileData.organisme.codePostal
                        })
                      });
                      if (!response.ok) {
                        throw new Error('Erreur lors de la mise à jour des informations d\'entreprise');
                      }
                      alert('Vous avez bien modifié vos informations');
                    } catch (error) {
                      console.error('Erreur:', error);
                      alert('Une erreur est survenue lors de la mise à jour des informations');
                    }
                  };
                  updateOrganismeInfo();
                }}
              />
            </div>
          ) : (
            <p className="info-text">
              {isPrestataireRoleLocal(user?.role) 
                ? "Pour des raisons contractuelles (engagement de 2 ans), les informations de l'entreprise ne peuvent pas être modifiées. Veuillez contacter le support pour toute demande de changement."
                : isClientRoleLocal(user?.role) && user?.role !== 'Acheteur signataire admin'
                  ? "Pour tout changement des informations d'entreprise, contactez votre responsable achat."
                  : "Pour tout changement des informations d'entreprise, contactez votre gestionnaire de compte."
              }
            </p>
          )}
        </div>
      </section>
    </>
  );

  // Onglet droits utilisateur
  const renderDroitsUtilisateur = () => (
    <ChangeRole organismeId={Number(profileData.organisme.id) || 0} />
  );

  // Onglet mes documents
  const renderMesDocuments = () => (
    <div className="profil-documents-page">
      <MesDocuments />
    </div>
  );

  // Importer le composant TeamViewOnly
  const TeamViewOnly = React.lazy(() => import('../../composants/TeamViewOnly/TeamViewOnly'));

  // Onglet gestion des comptes
  const renderGestionComptes = () => {
    // Seuls Prestataire signataire admin et Acheteur signataire admin peuvent voir la version avec actions
    // Tous les autres voient la version en lecture seule
    const canManageAccounts = user?.role === 'Prestataire signataire admin' || 
                              user?.role === 'Acheteur signataire admin';
    
    return (
      <React.Suspense fallback={<div>Chargement...</div>}>
        {canManageAccounts ? <GestionComptes /> : <TeamViewOnly />}
      </React.Suspense>
    );
  };

    // Nos propres implémentations de isClientRole et isPrestataireRole
    const isClientRoleLocal = (role: string | undefined): boolean => {
      if (!role) return false;
      return ['Acheteur', 'Acheteur signataire', 'Acheteur signataire admin'].includes(role);
    };

    const isPrestataireRoleLocal = (role: string | undefined): boolean => {
      if (!role) return false;
      return ['Prestataire', 'Prestataire signataire', 'Prestataire signataire admin'].includes(role);
    };

    // Déterminer quels onglets sont visibles selon le rôle
    const getVisibleTabs = () => {
      const userRole = user?.role;
      // console.log(userRole);

      // Onglets de base disponibles pour tous les utilisateurs
      const baseTabs = ["Mes Coordonnées"];
      
      // Ajouter l'onglet "Droits" pour tous les acheteurs
      if (isClientRoleLocal(userRole)) {
        baseTabs.push("Droits");
      }
      
      // Ajouter "Mon équipe" pour tous les prestataires et tous les acheteurs
      // En lecture seule pour tous sauf admin, en édition pour les admins
      if (userRole && (isPrestataireRoleLocal(userRole) || isClientRoleLocal(userRole))) {
        baseTabs.push("Mon équipe");
      }

      // Ajouter l'onglet "Mes documents" pour les gestionnaires
      if (userRole === 'Prestataire signataire admin') {
        baseTabs.push("Mes documents");
      }

      // Ajouter l'onglet "Mon besoin" pour les rôles clients
      if (isClientRoleLocal(userRole)) {
        baseTabs.push("Mon besoin");
      }

      // Ajouter l'onglet "Mes évaluations" pour les rôles prestataire spécifiques
      if (isPrestataireRoleLocal(userRole)) {
        baseTabs.push("Mes évaluations");
      }

      return baseTabs;
    };  // Obtenir les onglets visibles
  const visibleTabs = getVisibleTabs();

  // Déterminer quels onglets doivent être inactifs
  const getInactiveTabs = () => {
    const inactiveTabs: number[] = [];

    // L'onglet "Mon besoin" est inactif pour le moment
    const monBesoinIndex = visibleTabs.indexOf("Mon besoin");
    if (monBesoinIndex !== -1) {
      inactiveTabs.push(monBesoinIndex);
    }

    // L'onglet "Mes évaluations" est aussi inactif pour le moment
    const mesEvaluationsIndex = visibleTabs.indexOf("Mes évaluations");
    if (mesEvaluationsIndex !== -1) {
      inactiveTabs.push(mesEvaluationsIndex);
    }

    return inactiveTabs;
  };

  // Obtenir les onglets inactifs
  const inactiveTabs = getInactiveTabs();

  // Gérer le changement d'onglet avec index ajustés
  const handleTabChange = (tabIdx: number) => {
    // Stocker l'index réel dans le state
    setActiveTab(tabIdx);
    setActiveDocTab(null);
  };

  const renderMonBesoin = () => (
    <div className="profil-tab-page">
      <div className="coming-soon-message">
        <h2>Fonctionnalité à venir</h2>
        <p>Cette fonctionnalité sera disponible prochainement. Vous pourrez alors définir et gérer vos besoins spécifiques.</p>
      </div>
    </div>
  );

  // Déterminer quel contenu afficher selon l'onglet actif
  const renderTabContent = () => {

    switch (visibleTabs[activeTab]) {
      case "Mes Coordonnées":
        return renderCoordonnees();
      case "Droits":
        return renderDroitsUtilisateur();
      case "Mes documents":
        return renderMesDocuments();
      case "Mon équipe":
        return renderGestionComptes();
      case "Mon besoin":
        return renderMonBesoin();
      default:
        return null;
    }
  };

  const monBesoinMessage = "Cette fonctionnalité sera disponible prochainement";


  // Page principale avec onglets toujours visibles
  return (
    <div className="profil-container">
      <h1>Profil</h1>
      <div className="profil-tabs">
        <TabNav
          tabs={visibleTabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          inactiveTabs={inactiveTabs}
          inactiveTabsTooltips={{ "Mon besoin": monBesoinMessage }}
        />
      </div>
      {renderTabContent()}
    </div>
  );
};

export default Profil;